import { OptionalTruthyStringSchema, StringAsNumberSchema, isDefined, isTruthy, } from 'notable-common';
import { z } from 'zod';
import { FlowEntityType } from '../../../entity/entityType.js';
import { FlowRunExecutionMode } from '../../../flowRun/executionMode.js';
import { FlowBlockValueFieldSchema, FlowBlockValueFieldSchemaVariant, } from '../../blockValue/fieldSchema.js';
import { FlowBlockValueTemplateHint, validateFlowBlockValueTemplate, } from '../../blockValue/template.js';
import { FlowBlockValueTemplateDurationSchema } from '../../blockValue/templateDuration.js';
import { BaseFlowBlockSchema } from '../block.js';
import { FlowBlockType } from '../type.js';
import { FlowExportMode } from './shared/exportMode.js';
export var FlowFormBlockSchedulingConfigsState;
(function (FlowFormBlockSchedulingConfigsState) {
    FlowFormBlockSchedulingConfigsState["Enabled"] = "Enabled";
    FlowFormBlockSchedulingConfigsState["Disabled"] = "Disabled";
})(FlowFormBlockSchedulingConfigsState || (FlowFormBlockSchedulingConfigsState = {}));
export var FlowFormBlockPracticeType;
(function (FlowFormBlockPracticeType) {
    FlowFormBlockPracticeType["StaticList"] = "STATIC_LIST";
    FlowFormBlockPracticeType["Interpolated"] = "INTERPOLATED";
})(FlowFormBlockPracticeType || (FlowFormBlockPracticeType = {}));
export var FlowFormBlockProviderType;
(function (FlowFormBlockProviderType) {
    FlowFormBlockProviderType["StaticList"] = "STATIC_LIST";
    FlowFormBlockProviderType["DynamicList"] = "DYNAMIC_LIST";
    FlowFormBlockProviderType["Interpolated"] = "INTERPOLATED";
})(FlowFormBlockProviderType || (FlowFormBlockProviderType = {}));
export var FlowScheduleAppointmentDisplayBy;
(function (FlowScheduleAppointmentDisplayBy) {
    FlowScheduleAppointmentDisplayBy["Practice"] = "PRACTICE";
    FlowScheduleAppointmentDisplayBy["Provider"] = "PROVIDER";
})(FlowScheduleAppointmentDisplayBy || (FlowScheduleAppointmentDisplayBy = {}));
export var FlowScheduleAppointmentSortBy;
(function (FlowScheduleAppointmentSortBy) {
    FlowScheduleAppointmentSortBy["Alphabetically"] = "ALPHABETICALLY";
    FlowScheduleAppointmentSortBy["Availability"] = "AVAILABILITY";
    FlowScheduleAppointmentSortBy["Distance"] = "DISTANCE";
})(FlowScheduleAppointmentSortBy || (FlowScheduleAppointmentSortBy = {}));
export var FlowFormBlockAppointmentKindType;
(function (FlowFormBlockAppointmentKindType) {
    FlowFormBlockAppointmentKindType["StaticList"] = "StaticList";
    FlowFormBlockAppointmentKindType["Interpolated"] = "Interpolated";
})(FlowFormBlockAppointmentKindType || (FlowFormBlockAppointmentKindType = {}));
export const FlowFormBlockBaseConfigShape = z.object({
    formTemplateConfigs: z.array(z.object({
        formTemplateId: z.uuid({
            error: (issue) => isDefined(issue.input)
                ? 'Form template ID must be a valid UUID'
                : 'Form template ID is required',
        }),
        refreshInterval: FlowBlockValueTemplateDurationSchema().optional(),
    })),
    schedulingConfigs: z
        .object({
        schedulingConfigsState: z.enum(FlowFormBlockSchedulingConfigsState),
        scheduleAppointmentCallSectionEnabled: z.boolean(),
        scheduleAppointmentCommentEnabled: z.boolean(),
        fhirAppointmentKinds: z
            .array(OptionalTruthyStringSchema)
            .transform((value) => isTruthy(value.length) && isDefined(value[0]) ? [value[0]] : undefined)
            .optional(),
        appointmentKindType: z.enum(FlowFormBlockAppointmentKindType).optional(),
        practiceType: z.enum(FlowFormBlockPracticeType),
        practiceIds: z.array(z.string()).optional(),
        interpolatedPractice: FlowBlockValueFieldSchema({
            variant: FlowBlockValueFieldSchemaVariant.OptionalTemplate,
        })
            .transform((value) => value || undefined)
            .optional(),
        providerType: z.enum(FlowFormBlockProviderType),
        providerIds: z.array(z.string()).optional(),
        interpolatedProvider: FlowBlockValueFieldSchema({
            variant: FlowBlockValueFieldSchemaVariant.OptionalTemplate,
        })
            .transform((value) => value || undefined)
            .optional(),
        fhirDynamicProviders: z.array(z.string()).optional(),
        fhirTotalResult: StringAsNumberSchema(z.number().int().min(1).optional()),
        scheduleAppointmentDisplayBy: z.enum(FlowScheduleAppointmentDisplayBy).optional(),
        scheduleAppointmentSortBy: z.enum(FlowScheduleAppointmentSortBy).optional(),
        scheduleAppointmentStartTime: FlowBlockValueTemplateDurationSchema().optional(),
        scheduleAppointmentEndTime: FlowBlockValueTemplateDurationSchema().optional(),
        scheduleAppointmentDefaultReasonForVisit: OptionalTruthyStringSchema,
        customConfigs: z
            .object({
            scheduleAppointmentSeriesMinTime: FlowBlockValueTemplateDurationSchema().optional(),
            scheduleAppointmentSeriesMaxTime: FlowBlockValueTemplateDurationSchema().optional(),
            appointmentCommentModalLabel: OptionalTruthyStringSchema,
            appointmentCommentModalPlaceholder: OptionalTruthyStringSchema,
            appointmentCommentModalTitle: OptionalTruthyStringSchema,
            scheduleNextAvailableAppointmentEnabled: z.boolean().optional(),
            useReasonForVisitFromPatientOrders: z.boolean().optional(),
        })
            .optional(),
    })
        .superRefine((schedulingConfigs, context) => {
        if (schedulingConfigs.appointmentKindType === FlowFormBlockAppointmentKindType.Interpolated) {
            const kindValue = schedulingConfigs.fhirAppointmentKinds?.[0];
            if (isDefined(kindValue) && kindValue !== '') {
                const validationResult = validateFlowBlockValueTemplate(kindValue);
                if (validationResult.hint !== FlowBlockValueTemplateHint.Valid) {
                    context.addIssue({
                        code: 'custom',
                        message: 'message' in validationResult
                            ? validationResult.message
                            : 'Must be a valid interpolated variable. e.g. {{blockName.data.field}}',
                        path: ['fhirAppointmentKinds', 0],
                    });
                }
            }
        }
    })
        .optional(),
    /* eslint-disable-next-line @typescript-eslint/no-deprecated -- the block schema must reference the deprecated run-execution mode while it remains supported */
    runMode: z.enum(FlowRunExecutionMode).optional().default(FlowRunExecutionMode.Live),
    exportMode: z.enum(FlowExportMode).optional().default(FlowExportMode.RunAutoExport),
    dynamicParams: z
        .array(z.object({
        key: z.string().min(1),
        value: z.string().min(1),
    }))
        .refine((dynamicParams) => {
        const keys = dynamicParams.map((param) => param.key);
        return keys.length === new Set(keys).size;
    })
        .optional(),
    isOverridingEntity: z.boolean().optional(),
    entityOverride: z
        .object({
        entityType: z.enum(FlowEntityType),
        entityId: z.string(),
    })
        .optional(),
});
export function checkFlowFormBlockEntityOverride(config) {
    if (!config.isOverridingEntity) {
        return true; // Everything is ok
    }
    // We're overriding the entity, make sure it's specified
    const entity = config.entityOverride;
    if (!isDefined(entity)) {
        return false;
    }
    return isTruthy(entity.entityType) && isTruthy(entity.entityId);
}
export const flowFormBlockEntityOverrideRefineMessage = {
    message: 'When overriding the entity, both entityType and entityId must be specified.',
    path: ['entityOverride', 'entityType'],
};
export const FlowFormBlockSchema = BaseFlowBlockSchema.extend({
    type: z.literal(FlowBlockType.Form),
    config: FlowFormBlockBaseConfigShape.refine(checkFlowFormBlockEntityOverride, flowFormBlockEntityOverrideRefineMessage),
});
//# sourceMappingURL=config.js.map